import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { signInWithClever, linkAccountToClever, unlinkCleverAccount, checkCleverAuth } from './thunks/clever.thunk';

interface CleverState {
  isCleverUser: boolean;
  cleverUserData: any;
  isLoading: boolean;
  error: string | null;
  isLinking: boolean;
  isUnlinking: boolean;
}

const initialState: CleverState = {
  isCleverUser: false,
  cleverUserData: null,
  isLoading: false,
  error: null,
  isLinking: false,
  isUnlinking: false,
};

const cleverSlice = createSlice({
  name: 'clever',
  initialState,
  reducers: {
    clearCleverError: (state) => {
      state.error = null;
    },
    setCleverUserData: (state, action: PayloadAction<any>) => {
      state.cleverUserData = action.payload;
      state.isCleverUser = !!action.payload;
    },
    clearCleverData: (state) => {
      state.isCleverUser = false;
      state.cleverUserData = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Sign in with Clever
    builder
      .addCase(signInWithClever.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signInWithClever.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isCleverUser = true;
        state.cleverUserData = action.payload.user;
        state.error = null;
      })
      .addCase(signInWithClever.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Link account to Clever
    builder
      .addCase(linkAccountToClever.pending, (state) => {
        state.isLinking = true;
        state.error = null;
      })
      .addCase(linkAccountToClever.fulfilled, (state, action) => {
        state.isLinking = false;
        state.isCleverUser = true;
        state.cleverUserData = action.payload;
        state.error = null;
      })
      .addCase(linkAccountToClever.rejected, (state, action) => {
        state.isLinking = false;
        state.error = action.payload as string;
      });

    // Unlink Clever account
    builder
      .addCase(unlinkCleverAccount.pending, (state) => {
        state.isUnlinking = true;
        state.error = null;
      })
      .addCase(unlinkCleverAccount.fulfilled, (state, action) => {
        state.isUnlinking = false;
        state.isCleverUser = false;
        state.cleverUserData = null;
        state.error = null;
      })
      .addCase(unlinkCleverAccount.rejected, (state, action) => {
        state.isUnlinking = false;
        state.error = action.payload as string;
      });

    // Check Clever auth
    builder
      .addCase(checkCleverAuth.fulfilled, (state, action) => {
        state.isCleverUser = action.payload.isCleverUser;
        state.cleverUserData = action.payload.cleverUserData;
      });
  },
});

export const { clearCleverError, setCleverUserData, clearCleverData } = cleverSlice.actions;

export default cleverSlice.reducer; 